import React from 'react';
import { User, Calendar, Stethoscope } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

/**
 * QuickDemographicsTile — sleek inline mini-tile (Feb 26, 2026)
 *
 * The full demographics tile is collapsed inside ProviderInputCard. Provider
 * complained that Age + Gender are buried — they're the two fields she
 * touches every patient. This surfaces them right above the notes textarea,
 * alongside Encounter Type. Single source of truth (same setter functions
 * power both the buried tile and this one); no duplicate state.
 *
 * Design language: matches the dossier aesthetic — dark slate background,
 * subtle gold border, mono-style read-out values. Three compact controls
 * fit in one row on desktop, stack on mobile.
 */
const ENCOUNTER_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'established', label: 'Established' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'acute', label: 'Acute' },
];

const GENDER_OPTIONS = [
  { value: 'M', label: 'M — Male' },
  { value: 'F', label: 'F — Female' },
  { value: 'unspecified', label: '—' },
];

const QuickDemographicsTile = ({
  patientAge, setPatientAge,
  patientGender, setPatientGender,
  encounterType, setEncounterType,
}) => {
  return (
    <div
      className="flex flex-wrap items-center gap-2 px-2.5 py-2 rounded-lg border border-amber-500/25 bg-gradient-to-br from-slate-900/40 to-slate-800/30 shadow-inner"
      data-testid="quick-demographics-tile"
    >
      {/* Age */}
      <div className="flex items-center gap-1.5 flex-1 min-w-[100px]">
        <Calendar className="w-3 h-3 text-amber-400/70 shrink-0" />
        <label className="text-[10px] font-bold uppercase tracking-wider text-amber-400/70 shrink-0">
          Age
        </label>
        <input
          type="number"
          min={0}
          max={120}
          value={patientAge || ''}
          onChange={(e) => setPatientAge && setPatientAge(e.target.value ? Number(e.target.value) : null)}
          placeholder="—"
          className="flex-1 min-w-[40px] h-7 px-1.5 text-xs font-semibold bg-input border border-amber-500/20 rounded text-foreground placeholder:text-muted-foreground/50 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors text-center"
          data-testid="quick-demographics-age"
          aria-label="Patient age"
        />
      </div>

      {/* Gender */}
      <div className="flex items-center gap-1.5 flex-1 min-w-[110px]">
        <User className="w-3 h-3 text-amber-400/70 shrink-0" />
        <label className="text-[10px] font-bold uppercase tracking-wider text-amber-400/70 shrink-0">
          Sex
        </label>
        <Select
          value={patientGender || ''}
          onValueChange={(v) => setPatientGender && setPatientGender(v === 'unspecified' ? '' : v)}
        >
          <SelectTrigger
            className="flex-1 h-7 px-1.5 text-xs bg-input border-amber-500/20 hover:border-amber-500/40 transition-colors"
            data-testid="quick-demographics-gender"
            aria-label="Patient sex"
          >
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {GENDER_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} className="text-xs">
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Encounter Type */}
      <div className="flex items-center gap-1.5 flex-1 min-w-[140px]">
        <Stethoscope className="w-3 h-3 text-amber-400/70 shrink-0" />
        <label className="text-[10px] font-bold uppercase tracking-wider text-amber-400/70 shrink-0">
          Encounter
        </label>
        <Select
          value={encounterType || ''}
          onValueChange={(v) => setEncounterType && setEncounterType(v)}
        >
          <SelectTrigger
            className="flex-1 h-7 px-1.5 text-xs bg-input border-amber-500/20 hover:border-amber-500/40 transition-colors"
            data-testid="quick-demographics-encounter"
            aria-label="Encounter type"
          >
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {ENCOUNTER_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} className="text-xs">
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default QuickDemographicsTile;
